import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leadsAPI, nonprofitsAPI, usersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Loader2,
  MapPin,
  Phone,
  Mail,
  User,
  Zap,
  DollarSign,
  Heart,
  Building2,
  Home,
  Calendar,
  ChevronRight,
  Check,
  Lock,
  CreditCard,
  Upload,
  FileText,
  Download,
  X,
  File,
  Image as ImageIcon,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const MILESTONES = [
  'Lead Captured',
  'Qualified',
  'Proposal Sent',
  'Contract Signed',
  'Site Survey Completed',
  'Installation Scheduled',
  'Installation Completed',
  'PTO / Project Complete',
  'Sale Completed',
  'Rep Paid Out',
];

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, canManagePipeline } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [nonprofit, setNonprofit] = useState(null);
  const [salesRep, setSalesRep] = useState(null);
  const [manager, setManager] = useState(null);
  const [director, setDirector] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  // File uploads
  const [files, setFiles] = useState({ utility_bill: [], project_design: [], contract: [], proposal: [], other: [] });
  const [uploading, setUploading] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFileType, setUploadFileType] = useState('utility_bill');
  const [submittingDeal, setSubmittingDeal] = useState(false);

  useEffect(() => {
    loadLead();
    loadFiles();
  }, [id]);

  const loadLead = async () => {
    try {
      const response = await leadsAPI.getById(id);
      setLead(response.data);
      
      // Load related data
      if (response.data.nonprofit_partner_id) {
        try {
          const npRes = await nonprofitsAPI.getAll();
          const np = npRes.data.find(n => n.id === response.data.nonprofit_partner_id);
          setNonprofit(np);
        } catch {
          // User may not have permission to view nonprofits
        }
      }
      
      // Try to load team members (sales rep, manager, director)
      // This may fail for non-admin users, so we handle it gracefully
      try {
        const usersRes = await usersAPI.getAll();
        const allUsers = usersRes.data;
        
        if (response.data.assigned_sales_rep_id) {
          const rep = allUsers.find(r => r.id === response.data.assigned_sales_rep_id);
          setSalesRep(rep);
        }
        if (response.data.manager_id) {
          const mgr = allUsers.find(u => u.id === response.data.manager_id);
          setManager(mgr);
        }
        if (response.data.director_id) {
          const dir = allUsers.find(u => u.id === response.data.director_id);
          setDirector(dir);
        }
      } catch {
        // Non-admin users can't access users list - use lead's assigned_sales_rep_name
        // The lead response already includes commission_breakdown which has the important info
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to load lead details';
      toast.error(errorMessage);
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    try {
      const response = await leadsAPI.getFiles(id);
      setFiles(response.data.grouped || { utility_bill: [], project_design: [], contract: [], proposal: [], other: [] });
    } catch (error) {
      // Silently fail - files section will show empty state
    }
  };

  const handleFileUpload = async (event, fileType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setUploading(fileType);
    try {
      await leadsAPI.uploadFile(id, fileType, file);
      toast.success(`${file.name} uploaded successfully`);
      loadFiles(); // Reload files
      setUploadDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(null);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const response = await leadsAPI.downloadFile(id, file.id);
      const blob = new Blob([response.data], { type: file.content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleFileDelete = async (file) => {
    if (!window.confirm(`Are you sure you want to delete ${file.original_filename}?`)) {
      return;
    }
    
    try {
      await leadsAPI.deleteFile(id, file.id);
      toast.success('File deleted');
      loadFiles();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleMarkPaid = async () => {
    if (!paymentAmount || isNaN(parseFloat(paymentAmount))) {
      toast.error('Please enter a valid amount');
      return;
    }

    setUpdating(true);
    try {
      const response = await leadsAPI.markPaid(id, parseFloat(paymentAmount));
      toast.success(`Payment of $${parseFloat(paymentAmount).toLocaleString()} recorded`);
      setPaymentDialogOpen(false);
      setPaymentAmount('');
      loadLead(); // Reload to get updated amounts
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setUpdating(false);
    }
  };

  const handleMilestoneChange = async (newMilestone) => {
    if (!canManagePipeline() && user?.role !== 'SalesRep') return;
    if (lead.payout_locked && newMilestone !== lead.status) {
      toast.error('Cannot change milestone - payout is locked');
      return;
    }

    setUpdating(true);
    try {
      await leadsAPI.update(id, { status: newMilestone });
      setLead(prev => ({ ...prev, status: newMilestone }));
      toast.success(`Milestone updated to "${newMilestone}"`);
      loadLead(); // Reload to get updated payout status
    } catch (error) {
      toast.error('Failed to update milestone');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await leadsAPI.delete(id);
      toast.success('Lead deleted successfully');
      navigate('/leads');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleSubmitDeal = async () => {
    setSubmittingDeal(true);
    try {
      const response = await api.post(`/leads/${id}/submit-deal`);
      toast.success(response.data.message || 'Deal submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit deal. Make sure deal submission is configured in Settings.');
    } finally {
      setSubmittingDeal(false);
    }
  };

  const currentMilestoneIndex = MILESTONES.indexOf(lead?.status);

  const getMilestoneStyle = (index) => {
    if (index < currentMilestoneIndex) {
      return 'bg-emerald-600 text-white border-emerald-600';
    }
    if (index === currentMilestoneIndex) {
      return 'bg-emerald-100 text-emerald-700 border-emerald-500 ring-2 ring-emerald-500/20';
    }
    return 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
                {lead.customer_name}
              </h1>
              {lead.nonprofit_eligible && (
                <Badge className="sunraising-badge">
                  <Heart className="w-3 h-3 mr-1" />
                  SUNRAISING
                </Badge>
              )}
              {lead.payout_locked && (
                <Badge variant="outline" className="gap-1">
                  <Lock className="w-3 h-3" />
                  Locked
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 mt-2 text-slate-600 dark:text-slate-400">
              <span className="flex items-center gap-1">
                {lead.lead_type === 'Commercial' ? (
                  <Building2 className="w-4 h-4" />
                ) : (
                  <Home className="w-4 h-4" />
                )}
                {lead.lead_type}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {lead.city}, {lead.state}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
            onClick={handleSubmitDeal}
            disabled={submittingDeal}
            data-testid="submit-deal-btn"
          >
            {submittingDeal ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Submit Deal
          </Button>
          <Link to={`/leads/${id}/edit`}>
            <Button variant="outline" data-testid="edit-lead-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </Link>
          {isAdmin() && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-lead-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Lead</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this lead? This action cannot be undone.
                    All associated payout records will also be deleted.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Milestone Pipeline */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center justify-between">
            <span>Pipeline Status</span>
            <Badge className={lead.status === 'Sale Completed' ? 'bg-emerald-600 text-white' : ''}>
              {lead.status}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Progress line */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 dark:bg-slate-700" />
            <div
              className="absolute top-5 left-0 h-0.5 bg-emerald-600 transition-all duration-500"
              style={{ width: `${(currentMilestoneIndex / (MILESTONES.length - 1)) * 100}%` }}
            />
            
            {/* Milestones */}
            <div className="relative flex justify-between">
              {MILESTONES.map((milestone, index) => (
                <button
                  key={milestone}
                  onClick={() => !lead.payout_locked && handleMilestoneChange(milestone)}
                  disabled={updating || (lead.payout_locked && milestone !== lead.status)}
                  className={`flex flex-col items-center group ${
                    lead.payout_locked ? 'cursor-not-allowed' : 'cursor-pointer'
                  }`}
                  data-testid={`milestone-${index}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${getMilestoneStyle(index)} ${
                      !lead.payout_locked && 'group-hover:scale-110'
                    }`}
                  >
                    {index < currentMilestoneIndex ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                  <span className={`mt-2 text-xs text-center max-w-[80px] ${
                    index === currentMilestoneIndex ? 'font-medium text-emerald-700' : 'text-slate-500'
                  }`}>
                    {milestone}
                  </span>
                </button>
              ))}
            </div>
          </div>
          {updating && (
            <div className="flex items-center justify-center mt-4">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-600 mr-2" />
              <span className="text-sm text-slate-600">Updating milestone...</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Customer Name</p>
                  <p className="font-medium">{lead.customer_name}</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{lead.phone}</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{lead.email}</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Property Address</p>
                  <p className="font-medium">
                    {lead.property_address}<br />
                    {lead.city}, {lead.state} {lead.zip_code}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {lead.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                  {lead.notes}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Lead Created</p>
                    <p className="font-medium">
                      {new Date(lead.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
                {lead.contract_signed_date && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-amber-500" />
                    <div>
                      <p className="text-sm text-slate-500">Contract Signed</p>
                      <p className="font-medium">
                        {new Date(lead.contract_signed_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                )}
                {lead.install_complete_date && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-sm text-slate-500">Installation Completed</p>
                      <p className="font-medium">
                        {new Date(lead.install_complete_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                )}
                {lead.pto_date && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="text-sm text-slate-500">PTO / Project Complete</p>
                      <p className="font-medium">
                        {new Date(lead.pto_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* System & Payout */}
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-900/10 border-emerald-200 dark:border-emerald-800">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
                <Zap className="w-5 h-5" />
                System & Payouts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">System Size</p>
                <p className="text-3xl font-bold font-mono text-emerald-800 dark:text-emerald-200">
                  {lead.system_size_kw} kW
                </p>
              </div>
              
              {/* PPW Sold - For Red Line */}
              {lead.ppw_sold && (
                <div>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">Deal Price Per Watt</p>
                  <p className="text-xl font-bold font-mono text-emerald-800 dark:text-emerald-200">
                    ${lead.ppw_sold.toFixed(2)}/W
                  </p>
                </div>
              )}
              
              <Separator className="bg-emerald-200 dark:bg-emerald-800" />
              <div>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">Sales Rep Commission</p>
                <p className="text-2xl font-bold font-mono text-emerald-800 dark:text-emerald-200">
                  ${lead.commission_rep_amount.toLocaleString()}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`text-xs ${
                    lead.payout_status_rep === 'Paid' ? 'status-paid' :
                    lead.payout_status_rep === 'Due' ? 'status-due' : 'status-not-due'
                  }`}>
                    {lead.payout_status_rep}
                  </Badge>
                </div>
              </div>
              
              {/* Amount Paid / Due Section */}
              <Separator className="bg-emerald-200 dark:bg-emerald-800" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">Amount Paid</p>
                  <p className="text-lg font-bold font-mono text-emerald-800 dark:text-emerald-200">
                    ${(lead.amount_paid || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">Amount Due</p>
                  <p className={`text-lg font-bold font-mono ${
                    (lead.amount_due || lead.commission_rep_amount) > 0 
                      ? 'text-amber-600' 
                      : 'text-emerald-800 dark:text-emerald-200'
                  }`}>
                    ${(lead.amount_due !== undefined ? lead.amount_due : lead.commission_rep_amount).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {/* Mark Paid Button - Admin/BackOffice only */}
              {(isAdmin() || user?.role === 'BackOffice') && lead.status === 'Sale Completed' && lead.payout_status_rep !== 'Paid' && (
                <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="mark-paid-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Record Payment
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Record Payment</DialogTitle>
                      <DialogDescription>
                        Record a payment for this deal. The amount due is ${((lead.amount_due !== undefined ? lead.amount_due : lead.commission_rep_amount) || 0).toLocaleString()}.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Payment Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          placeholder="Enter amount"
                          data-testid="payment-amount-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPaymentAmount(String((lead.amount_due !== undefined ? lead.amount_due : lead.commission_rep_amount) || 0))}
                        >
                          Full Amount
                        </Button>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleMarkPaid}
                        disabled={updating}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="confirm-payment-btn"
                      >
                        {updating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Record Payment'
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
              
              {lead.nonprofit_eligible && (
                <>
                  <Separator className="bg-emerald-200 dark:bg-emerald-800" />
                  <div>
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">Nonprofit Donation</p>
                    <p className="text-2xl font-bold font-mono text-emerald-800 dark:text-emerald-200">
                      ${lead.donation_nonprofit_amount.toLocaleString()}
                    </p>
                    <p className="text-xs text-rose-600 mt-1">(Deducted from rep commission)</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`text-xs ${
                        lead.payout_status_nonprofit === 'Paid' ? 'status-paid' :
                        lead.payout_status_nonprofit === 'Due' ? 'status-due' : 'status-not-due'
                      }`}>
                        {lead.payout_status_nonprofit}
                      </Badge>
                    </div>
                  </div>
                </>
              )}
              
              {/* Free DC Solar Badge */}
              {lead.is_dc_market && lead.is_free_dc_solar && (
                <>
                  <Separator className="bg-emerald-200 dark:bg-emerald-800" />
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-center">
                    <Badge className="bg-blue-600 text-white">
                      <Zap className="w-3 h-3 mr-1" />
                      FREE DC Solar ($200/kW)
                    </Badge>
                  </div>
                </>
              )}
              
              {/* Red Line Commission Display */}
              {lead.commission_breakdown?.redline_commission > 0 && (
                <>
                  <Separator className="bg-emerald-200 dark:bg-emerald-800" />
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1">
                      <Zap className="w-4 h-4" />
                      Director Red Line Commission
                    </p>
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Gross:</span>
                        <span className="font-mono font-medium">${lead.commission_breakdown.redline_commission.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Team Deductions:</span>
                        <span className="font-mono text-red-600">-${(lead.commission_breakdown.redline_commission - lead.commission_breakdown.redline_net).toLocaleString()}</span>
                      </div>
                      <Separator className="bg-amber-200 dark:bg-amber-800 my-1" />
                      <div className="flex justify-between font-medium">
                        <span className="text-amber-700 dark:text-amber-300">Net Payout:</span>
                        <span className="font-mono text-amber-700 dark:text-amber-300">${lead.commission_breakdown.redline_net.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Assignments & Commission Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Team Assignments & Commissions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Sales Rep */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-slate-500">Sales Representative</p>
                    <p className="font-medium">
                      {salesRep?.name || lead.assigned_sales_rep_name || 'Unassigned'}
                    </p>
                    {salesRep?.rep_tier && (
                      <p className="text-xs text-slate-400 mt-1">{salesRep.rep_tier}</p>
                    )}
                  </div>
                  {lead.commission_breakdown?.rep_commission > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Commission</p>
                      <p className="font-mono font-semibold text-emerald-600">
                        ${lead.commission_breakdown.rep_commission.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Manager */}
              {(lead.manager_id || manager) && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-amber-600 dark:text-amber-400">Manager</p>
                      <p className="font-medium">
                        {manager?.name || lead.manager_name || 'Not assigned'}
                      </p>
                    </div>
                    {lead.commission_breakdown?.manager_override > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-amber-600 dark:text-amber-400">Override Commission</p>
                        <p className="font-mono font-semibold text-amber-700 dark:text-amber-300">
                          ${lead.commission_breakdown.manager_override.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Director */}
              {(lead.director_id || director) && (
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-purple-600 dark:text-purple-400">Director</p>
                      <p className="font-medium">
                        {director?.name || lead.director_name || 'Not assigned'}
                      </p>
                    </div>
                    <div className="text-right">
                      {lead.commission_breakdown?.redline_net > 0 ? (
                        <>
                          <p className="text-xs text-purple-600 dark:text-purple-400">Red Line Net</p>
                          <p className="font-mono font-semibold text-purple-700 dark:text-purple-300">
                            ${lead.commission_breakdown.redline_net.toLocaleString()}
                          </p>
                        </>
                      ) : lead.commission_breakdown?.director_override > 0 ? (
                        <>
                          <p className="text-xs text-purple-600 dark:text-purple-400">Override Commission</p>
                          <p className="font-mono font-semibold text-purple-700 dark:text-purple-300">
                            ${lead.commission_breakdown.director_override.toLocaleString()}
                          </p>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              {/* Lead Source */}
              {lead.lead_source && (
                <div>
                  <p className="text-sm text-slate-500">Lead Source</p>
                  <p className="font-medium">{lead.lead_source}</p>
                </div>
              )}
              
              {/* Nonprofit Partner */}
              {lead.nonprofit_eligible && nonprofit && (
                <div>
                  <p className="text-sm text-slate-500">Nonprofit Partner</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Heart className="w-4 h-4 text-rose-500" />
                    <p className="font-medium">{nonprofit.name}</p>
                  </div>
                </div>
              )}

              {/* Commission Breakdown Details */}
              {lead.commission_breakdown?.breakdown_details?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Commission Breakdown</p>
                  <div className="space-y-2">
                    {lead.commission_breakdown.breakdown_details.map((detail, idx) => (
                      <div key={`detail-${idx}-${detail.type}`} className="flex justify-between text-sm">
                        <span className="text-slate-500">
                          {detail.type}
                          {detail.rate_per_kw && ` (${detail.rate_per_kw}/kW × ${detail.kw}kW)`}
                        </span>
                        <span className="font-mono">${detail.amount?.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm font-semibold pt-2 border-t border-slate-200 dark:border-slate-700">
                      <span>Total Payout</span>
                      <span className="font-mono text-emerald-600">
                        ${lead.commission_breakdown.total_payout?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* File Upload Sections - Full Width */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Utility Bill Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                Utility Bill
              </span>
              <label className="cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  onChange={(e) => handleFileUpload(e, 'utility_bill')}
                  disabled={uploading === 'utility_bill'}
                  data-testid="upload-utility-bill-input"
                />
                <Button 
                  size="sm" 
                  disabled={uploading === 'utility_bill'}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  asChild
                >
                  <span>
                    {uploading === 'utility_bill' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </span>
                </Button>
              </label>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {files.utility_bill.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No utility bill uploaded yet</p>
                <p className="text-xs mt-1">Upload customer&apos;s utility bill for deal processing</p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.utility_bill.map((file) => (
                  <div 
                    key={file.id} 
                    className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {file.content_type?.startsWith('image/') ? (
                        <ImageIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      ) : (
                        <File className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{file.original_filename}</p>
                        <p className="text-xs text-slate-500">
                          Uploaded by {file.uploaded_by_name} • {new Date(file.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleFileDownload(file)}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {(isAdmin() || user?.role === 'BackOffice') && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleFileDelete(file)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Project Design Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Home className="w-5 h-5 text-blue-500" />
                Project Design
              </span>
              <label className="cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.dwg"
                  onChange={(e) => handleFileUpload(e, 'project_design')}
                  disabled={uploading === 'project_design'}
                  data-testid="upload-project-design-input"
                />
                <Button 
                  size="sm" 
                  disabled={uploading === 'project_design'}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  asChild
                >
                  <span>
                    {uploading === 'project_design' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </span>
                </Button>
              </label>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {files.project_design.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Home className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No project design uploaded yet</p>
                <p className="text-xs mt-1">Upload solar panel layout and design documents</p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.project_design.map((file) => (
                  <div 
                    key={file.id} 
                    className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {file.content_type?.startsWith('image/') ? (
                        <ImageIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      ) : (
                        <File className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{file.original_filename}</p>
                        <p className="text-xs text-slate-500">
                          Uploaded by {file.uploaded_by_name} • {new Date(file.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleFileDownload(file)}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {(isAdmin() || user?.role === 'BackOffice') && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleFileDelete(file)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contract Documents Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-500" />
                Contract Documents
              </span>
              <label className="cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  onChange={(e) => handleFileUpload(e, 'contract')}
                  disabled={uploading === 'contract'}
                  data-testid="upload-contract-input"
                />
                <Button 
                  size="sm" 
                  disabled={uploading === 'contract'}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  asChild
                >
                  <span>
                    {uploading === 'contract' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </span>
                </Button>
              </label>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {files.contract.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No contract uploaded yet</p>
                <p className="text-xs mt-1">Upload signed contracts and agreements</p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.contract.map((file) => (
                  <div 
                    key={file.id} 
                    className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <File className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{file.original_filename}</p>
                        <p className="text-xs text-slate-500">
                          Uploaded by {file.uploaded_by_name} • {new Date(file.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleFileDownload(file)}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {(isAdmin() || user?.role === 'BackOffice') && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleFileDelete(file)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Proposal Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-500" />
                Proposal
              </span>
              <label className="cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  onChange={(e) => handleFileUpload(e, 'proposal')}
                  disabled={uploading === 'proposal'}
                  data-testid="upload-proposal-input"
                />
                <Button 
                  size="sm" 
                  disabled={uploading === 'proposal'}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  asChild
                >
                  <span>
                    {uploading === 'proposal' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </span>
                </Button>
              </label>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {files.proposal.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No proposal uploaded yet</p>
                <p className="text-xs mt-1">Upload the solar proposal for this deal</p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.proposal.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-8 h-8 text-orange-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{file.original_filename}</p>
                        <p className="text-xs text-slate-500">
                          Uploaded by {file.uploaded_by_name} • {new Date(file.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleFileDownload(file)}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {(isAdmin() || user?.role === 'BackOffice') && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleFileDelete(file)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Other Documents Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <File className="w-5 h-5 text-purple-500" />
                Other Documents
              </span>
              <label className="cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  onChange={(e) => handleFileUpload(e, 'other')}
                  disabled={uploading === 'other'}
                  data-testid="upload-other-input"
                />
                <Button 
                  size="sm" 
                  disabled={uploading === 'other'}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  asChild
                >
                  <span>
                    {uploading === 'other' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </span>
                </Button>
              </label>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {files.other.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <File className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No other documents uploaded</p>
                <p className="text-xs mt-1">Upload any additional supporting documents</p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.other.map((file) => (
                  <div 
                    key={file.id} 
                    className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {file.content_type?.startsWith('image/') ? (
                        <ImageIcon className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      ) : (
                        <File className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{file.original_filename}</p>
                        <p className="text-xs text-slate-500">
                          Uploaded by {file.uploaded_by_name} • {new Date(file.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleFileDownload(file)}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {(isAdmin() || user?.role === 'BackOffice') && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleFileDelete(file)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LeadDetail;
